<template>
  <div
    v-if="active"
    ref="floatRef"
    class="pm-slash"
    :style="floatStyle"
    role="listbox"
    aria-label="Insert block"
  >
    <div v-if="filtered.length === 0" class="pm-slash__empty">No blocks match</div>
    <button
      v-for="(item, idx) in filtered"
      :key="item.id"
      type="button"
      class="pm-slash__item"
      :class="{ 'is-active': idx === selected }"
      :data-testid="`slash-${item.id}`"
      @mousedown.prevent
      @mouseenter="selected = idx"
      @click="pick(idx)"
    >
      <span class="pm-slash__icon" :class="`pi ${item.icon}`"></span>
      <span class="pm-slash__body">
        <span class="pm-slash__title">{{ item.title }}</span>
        <span class="pm-slash__sub">{{ item.subtitle }}</span>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  ref, computed, watch, shallowRef,
} from 'vue';
import { useFloating, autoUpdate, flip, shift, offset } from '@floating-ui/vue';
import {
  setBlockType,
  wrapIn,
} from 'prosemirror-commands';
import { wrapInList } from 'prosemirror-schema-list';
import type { EditorView } from 'prosemirror-view';
import { pmSchema } from './pmSchema';

interface SlashItem {
  id: string
  title: string
  subtitle: string
  icon: string
  /** Returns true if it ran successfully. The slash range has already been deleted. */
  run: (view: EditorView) => boolean
}

const props = defineProps<{
  view: EditorView | null
  active: boolean
  query: string
  from: number
  to: number
  rect: DOMRect | null
}>();

const emit = defineEmits<{
  (e: 'pick', item: { id: string }): void
  (e: 'dismiss'): void
}>();

const floatRef = ref<HTMLElement | null>(null);
const virtualRef = computed(() => {
  const r = props.rect;
  if (!r) return null;
  return { getBoundingClientRect: () => r };
});
const reference = shallowRef<{ getBoundingClientRect: () => DOMRect } | null>(null);
watch(virtualRef, (v) => { reference.value = v; }, { immediate: true });

const { floatingStyles } = useFloating(reference, floatRef, {
  placement: 'bottom-start',
  middleware: [offset(8), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
});
const floatStyle = computed(() => floatingStyles.value);

const selected = ref(0);
watch(() => props.active, (v) => { if (v) selected.value = 0; });
watch(() => props.query, () => { selected.value = 0; });

const allItems = computed<SlashItem[]>(() => {
  const s = pmSchema;
  const items: SlashItem[] = [];
  if (s.nodes.heading) {
    [1, 2, 3].forEach((level) => {
      items.push({
        id: `h${level}`,
        title: `Heading ${level}`,
        subtitle: `${'#'.repeat(level)} Heading text`,
        icon: 'pi-hashtag',
        run: (view) => setBlockType(s.nodes.heading, { level })(view.state, view.dispatch, view),
      });
    });
  }
  if (s.nodes.bullet_list) {
    items.push({
      id: 'bullet',
      title: 'Bulleted list',
      subtitle: 'A simple unordered list',
      icon: 'pi-list',
      run: (view) => wrapInList(s.nodes.bullet_list)(view.state, view.dispatch, view),
    });
  }
  if (s.nodes.ordered_list) {
    items.push({
      id: 'ordered',
      title: 'Numbered list',
      subtitle: '1. 2. 3. …',
      icon: 'pi-sort-numeric-down',
      run: (view) => wrapInList(s.nodes.ordered_list)(view.state, view.dispatch, view),
    });
  }
  if (s.nodes.task_list) {
    items.push({
      id: 'task',
      title: 'Task list',
      subtitle: 'Track to-dos with checkboxes',
      icon: 'pi-check-square',
      run: (view) => {
        const taskList = s.nodes.task_list;
        const taskItem = s.nodes.task_item;
        const paragraph = s.nodes.paragraph;
        const node = taskList.create({}, taskItem.create({ checked: false }, paragraph.create()));
        view.dispatch(view.state.tr.replaceSelectionWith(node));
        return true;
      },
    });
  }
  if (s.nodes.blockquote) {
    items.push({
      id: 'quote',
      title: 'Quote',
      subtitle: 'A short callout',
      icon: 'pi-comment',
      run: (view) => wrapIn(s.nodes.blockquote)(view.state, view.dispatch, view),
    });
  }
  if (s.nodes.code_block) {
    items.push({
      id: 'code',
      title: 'Code block',
      subtitle: 'Preformatted text',
      icon: 'pi-code',
      run: (view) => setBlockType(s.nodes.code_block)(view.state, view.dispatch, view),
    });
  }
  if (s.nodes.horizontal_rule) {
    items.push({
      id: 'hr',
      title: 'Divider',
      subtitle: 'A horizontal rule',
      icon: 'pi-minus',
      run: (view) => {
        view.dispatch(view.state.tr.replaceSelectionWith(s.nodes.horizontal_rule.create()));
        return true;
      },
    });
  }
  if (s.nodes.table) {
    items.push({
      id: 'table',
      title: 'Table 3 × 3',
      subtitle: '3 rows, 3 columns',
      icon: 'pi-table',
      run: (view) => {
        const tableType = s.nodes.table;
        const rowType = s.nodes.table_row;
        const cellType = s.nodes.table_cell;
        const headerType = s.nodes.table_header ?? cellType;
        if (!tableType || !rowType || !cellType) return false;
        const para = () => s.nodes.paragraph.create();
        const headerRow = rowType.create(null, Array.from({ length: 3 }, () => headerType.create(null, para())));
        const bodyRow = () => rowType.create(null, Array.from({ length: 3 }, () => cellType.create(null, para())));
        const tableNode = tableType.create(null, [headerRow, bodyRow(), bodyRow()]);
        view.dispatch(view.state.tr.replaceSelectionWith(tableNode));
        return true;
      },
    });
  }
  if (s.nodes.image) {
    items.push({
      id: 'image',
      title: 'Image',
      subtitle: 'Insert from URL',
      icon: 'pi-image',
      run: (view) => {
        const url = window.prompt('Image URL', 'https://');
        if (!url) return false;
        view.dispatch(view.state.tr.replaceSelectionWith(s.nodes.image.create({ src: url })));
        return true;
      },
    });
  }
  return items;
});

const filtered = computed(() => {
  const q = (props.query || '').toLowerCase().trim();
  if (!q) return allItems.value;
  return allItems.value.filter((it) =>
    it.title.toLowerCase().includes(q) || it.id.toLowerCase().includes(q));
});

function pick(idx: number) {
  const view = props.view;
  if (!view) return;
  const item = filtered.value[idx];
  if (!item) return;
  // Delete the `/query` first, then run the command on the resulting state.
  const tr = view.state.tr.delete(props.from, props.to);
  view.dispatch(tr);
  // Re-run on the updated view.
  item.run(view);
  view.focus();
  emit('pick', { id: item.id });
}

defineExpose({
  /** Called by the plugin via the parent. */
  handleKey(key: string): boolean {
    if (!props.active) return false;
    if (key === 'ArrowDown') {
      selected.value = (selected.value + 1) % Math.max(1, filtered.value.length);
      return true;
    }
    if (key === 'ArrowUp') {
      selected.value = (selected.value - 1 + Math.max(1, filtered.value.length))
        % Math.max(1, filtered.value.length);
      return true;
    }
    if (key === 'Enter') {
      if (filtered.value.length === 0) return false;
      pick(selected.value);
      return true;
    }
    if (key === 'Escape') {
      emit('dismiss');
      return true;
    }
    return false;
  },
});
</script>

<style scoped>
.pm-slash {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 60;
  width: 280px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
  padding: 4px;
  font-family: 'Outfit', system-ui, sans-serif;
}

.pm-slash__empty {
  padding: 12px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
}

.pm-slash__item {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  color: #111827;
}

.pm-slash__item.is-active,
.pm-slash__item:hover {
  background: #f3f4f6;
}

.pm-slash__icon {
  flex-shrink: 0;
  margin-top: 2px;
  width: 18px;
  text-align: center;
  color: #6b7280;
}

.pm-slash__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pm-slash__title {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}

.pm-slash__sub {
  font-size: 11px;
  color: #6b7280;
}
</style>
